import { useMemo, useState } from "react";
import { Pencil, X } from "lucide-react";
import type { Section, ContentItem, ContentRiskLevel } from "../../types/contractTemplate";
import { CONTENT_RISK_LEVELS } from "../../types/contractTemplate";
import { useBusinessUnits } from "../../hooks/useBusinessUnits";
import { formatDisplayDate } from "../../utils/displayDate";
import { sanitizeTableHtmlFragment } from "../../utils/sanitizeTableHtml";
import { tryParsePlainTableGrid } from "../../utils/parsePlainTableText";
import { Button } from "../atoms/Button";

const contentToText = (content: ContentItem[]): string =>
  content.map((c) => c.text).join("\n\n");

const textToContent = (text: string): ContentItem[] =>
  [{ type: "clause" as const, text }];

const splitParagraphParts = (text: string): string[] => {
  const normalized = text.replace(/\r\n|\r/g, "\n").replace(/\t/g, " ");
  return normalized
    .split(/\n+/)
    .map((p) => p.trim())
    .filter((p) => p.length > 0);
};

type TableBlockProps = {
  readonly plain: string;
  readonly html?: string;
  readonly index: number;
};

const tableChromeFooter = (
  <p className="border-t border-gray-200 px-3 py-1.5 text-[11px] text-gray-500">
    Scroll sideways on wide tables.
  </p>
);

const TableBlockView = ({ plain, html, index }: TableBlockProps) => {
  const safe = useMemo(() => {
    if (!html?.trim()) return "";
    return sanitizeTableHtmlFragment(html);
  }, [html]);

  const parsedRows = useMemo(() => tryParsePlainTableGrid(plain), [plain]);

  const keySeed = plain.slice(0, 24) + index;

  if (safe) {
    return (
      <div className="mb-4 overflow-hidden rounded-md border border-gray-200 bg-white">
        <div className="max-h-[min(82vh,50rem)] overflow-x-auto overflow-y-auto">
          <div
            className="clause-table-ui min-w-max px-1 py-1.5 text-sm text-gray-900 sm:px-2 [&_caption]:mb-2 [&_caption]:px-2 [&_caption]:text-left [&_caption]:text-xs [&_caption]:font-medium [&_caption]:text-gray-600 [&_table]:min-w-[18rem] [&_table]:border-collapse [&_table]:text-left [&_td]:min-w-[7rem] [&_td]:border [&_td]:border-gray-300 [&_td]:px-2.5 [&_td]:py-1.5 [&_td]:align-top [&_th]:min-w-[7rem] [&_th]:border [&_th]:border-gray-300 [&_th]:bg-gray-50 [&_th]:px-2.5 [&_th]:py-1.5 [&_th]:text-left [&_th]:text-sm [&_th]:font-semibold [&_th]:text-gray-800"
            // Safe: sanitized to table family only, no scripts/inline handlers.
            dangerouslySetInnerHTML={{ __html: safe }}
          />
        </div>
        {tableChromeFooter}
      </div>
    );
  }

  if (parsedRows && parsedRows.length > 0) {
    const maxCols = parsedRows[0].length;
    const useThead = parsedRows.length >= 2 && maxCols >= 2;
    const headRow = useThead ? parsedRows[0] : null;
    const bodyRows = useThead ? parsedRows.slice(1) : parsedRows;

    return (
      <div className="mb-4 overflow-hidden rounded-md border border-gray-200 bg-white">
        <div className="max-h-[min(82vh,50rem)] overflow-x-auto overflow-y-auto">
          <table className="w-full min-w-[min(100%,20rem)] border-collapse text-left text-sm text-gray-900">
            {headRow ? (
              <thead>
                <tr className="bg-gray-50">
                  {headRow.map((cell, j) => (
                    <th
                      key={`h-${index}-${j}`}
                      scope="col"
                      className="min-w-[7rem] border border-gray-300 px-2.5 py-1.5 text-sm font-semibold text-gray-800 first:pl-3 last:pr-3"
                    >
                      {cell || "\u00a0"}
                    </th>
                  ))}
                </tr>
              </thead>
            ) : null}
            <tbody>
              {bodyRows.map((row, i) => (
                <tr
                  key={`r-${index}-${i}`}
                  className={i % 2 === 1 ? "bg-gray-50/40" : "bg-white"}
                >
                  {row.map((cell, j) => (
                    <td
                      key={`c-${index}-${i}-${j}`}
                      className="min-w-[7rem] border border-gray-300 px-2.5 py-1.5 align-top first:pl-3 last:pr-3"
                    >
                      {cell || "\u00a0"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {tableChromeFooter}
      </div>
    );
  }

  return (
    <div className="mb-6 overflow-hidden rounded-xl border border-slate-200/90 bg-slate-50 shadow-sm ring-1 ring-slate-900/5">
      <pre
        className="m-0 max-h-[min(82vh,50rem)] overflow-auto whitespace-pre-wrap break-words p-4 font-mono text-xs leading-relaxed text-slate-800"
        key={keySeed}
      >
        {plain}
      </pre>
      {tableChromeFooter}
    </div>
  );
};

export type ClauseCardProps = {
  readonly section: Section;
  readonly sectionIndex: number;
  readonly isEditing: boolean;
  readonly draftContent: ContentItem[] | null;
  readonly selectedBUs?: string[];
  readonly excluded?: boolean;
  readonly onEditStart: () => void;
  readonly onDraftChange: (content: ContentItem[]) => void;
  readonly onEditSave: () => void;
  readonly onEditCancel: () => void;
  readonly onBUsChange?: (bu: string[]) => void;
  readonly onExcludeChange?: (exclude: boolean) => void;
  readonly contentRiskLevel?: ContentRiskLevel;
  readonly onContentRiskLevelChange?: (level: ContentRiskLevel) => void;
  readonly validationError: string | null;
  readonly hasUnsavedEdits?: boolean;
  /** When set, controls Save enablement (e.g. allow approve when this contract has no active version). Cancel still uses `hasUnsavedEdits`. */
  readonly sectionSaveEnabled?: boolean;
  readonly isSaving?: boolean;
  readonly onSectionSave?: () => void;
  readonly onSectionCancel?: () => void;
};

const normalizeToLabels = (raw: string[], allUnits: readonly string[]): string[] => {
  const normalized = raw.map((bu) => bu.trim().toLowerCase());
  const hasAllBus =
    normalized.includes("all bus") ||
    normalized.includes("all bus.") ||
    normalized.includes("all buss") ||
    normalized.includes("all bus ") ||
    normalized.includes("all bus.".replace(".", ""));
  if (!raw.length) return [];
  if (hasAllBus) return allUnits.filter((label) => label !== "All BUs");
  const fromPayload = allUnits.filter((label) =>
    normalized.includes(label.toLowerCase())
  );
  return fromPayload.length > 0 ? fromPayload : ["On Call"];
};

export const ClauseCard = ({
  section,
  isEditing,
  draftContent,
  selectedBUs: controlledBUs,
  excluded: controlledExcluded,
  onEditStart,
  onDraftChange,
  onEditSave,
  onEditCancel,
  onBUsChange,
  onExcludeChange,
  contentRiskLevel = "BusinessDecision",
  onContentRiskLevelChange,
  validationError,
  hasUnsavedEdits,
  sectionSaveEnabled,
  isSaving,
  onSectionSave,
  onSectionCancel,
}: ClauseCardProps) => {
  const saveAllowed = sectionSaveEnabled ?? hasUnsavedEdits ?? false;
  const { options: businessUnitOptions } = useBusinessUnits();
  const allBusinessUnits = useMemo(
    () => businessUnitOptions.map((o) => o.name),
    [businessUnitOptions]
  );
  const [internalExcluded, setInternalExcluded] = useState(false);
  const [internalBUs, setInternalBUs] = useState<string[]>(() =>
    normalizeToLabels(section.businessUnit ?? [], allBusinessUnits)
  );

  const isControlled = controlledBUs !== undefined && controlledExcluded !== undefined;
  const selectedBUs = isControlled ? controlledBUs : internalBUs;
  const excluded = isControlled ? (controlledExcluded ?? false) : internalExcluded;

  const setSelectedBUsState = (updater: (prev: string[]) => string[]) => {
    if (onBUsChange) {
      const next = updater(selectedBUs);
      onBUsChange(next);
    } else {
      setInternalBUs(updater);
    }
  };

  const setExcludedState = (value: boolean) => {
    if (onExcludeChange) {
      onExcludeChange(value);
    } else {
      setInternalExcluded(value);
    }
  };

  const toggleBusinessUnit = (label: string) => {
    setSelectedBUsState((prev) => {
      if (label === "All BUs") {
        return allBusinessUnits.filter((unit) => unit !== "All BUs");
      }

      const isSelected = prev.includes(label);
      let next = prev.filter((bu) => bu !== label && bu !== "All BUs");

      if (!isSelected) {
        next = [...next, label];
      }

      if (next.length === 0) {
        return [];
      }

      return next;
    });
  };

  const displayContent = isEditing && draftContent ? draftContent : section.content;
  const hasTableBlocks = (section.content || []).some((item) => item.type === "table");

  const updateBlockText = (index: number, text: string) => {
    const base = draftContent ?? section.content;
    const next = base.map((item, i) =>
      i === index && item.type !== "table" ? { ...item, text } : item
    );
    onDraftChange(next);
  };

  const renderReadableBlocks = (items: ContentItem[]) => (
    <div className="space-y-1">
      {items.flatMap((item, idx) => {
        if (item.type === "table") {
          return [
            <TableBlockView
              key={`table-${idx}-${item.text.slice(0, 20)}`}
              plain={item.text}
              html={item.textAsHtml}
              index={idx}
            />,
          ];
        }
        return splitParagraphParts(item.text).map((text, pIdx) => (
          <p
            key={`${item.type}-${idx}-${pIdx}-${text.slice(0, 20)}`}
            className={
              item.type === "list_item"
                ? "mb-3 ml-4 before:mr-2 before:content-['•']"
                : "mb-3 last:mb-0"
            }
          >
            {text}
          </p>
        ));
      })}
    </div>
  );

  const renderBlockEditors = (items: ContentItem[]) => (
    <div className="space-y-4">
      {items.map((item, idx) => {
        if (item.type === "table") {
          return (
            <div key={`edit-table-${idx}`} className="rounded-lg border border-amber-200 bg-amber-50/60 p-3">
              <p className="mb-2 text-xs font-medium text-amber-900">Table (preview — not editable as grid)</p>
              <TableBlockView plain={item.text} html={item.textAsHtml} index={idx} />
            </div>
          );
        }
        return (
          <div key={`edit-block-${idx}`}>
            <label className="mb-1 block text-xs font-medium text-gray-500">
              {item.type === "list_item" ? "List item" : "Paragraph"}
            </label>
            <textarea
              value={item.text}
              onChange={(e) => updateBlockText(idx, e.target.value)}
              rows={Math.min(16, Math.max(3, item.text.split("\n").length + 2))}
              className="w-full resize-y rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm leading-relaxed text-gray-800 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="flex max-h-[min(94vh,72rem)] flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="shrink-0 border-b border-gray-100 px-6 pb-3 pt-6">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <span className="text-base font-semibold leading-snug text-heading">{section.sectionName}</span>
            <span className="text-xs text-gray-500">
              {section.content[0]?.createdAt
                ? formatDisplayDate(section.content[0].createdAt)
                : "—"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {isEditing ? (
              <>
                <button
                  type="button"
                  onClick={onEditCancel}
                  className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-600 shadow-sm transition-colors hover:bg-gray-50"
                >
                  <X className="h-3 w-3" />
                  Cancel edit
                </button>
                <button
                  type="button"
                  onClick={onEditSave}
                  className="inline-flex items-center gap-1 rounded-md border border-primary-600 bg-primary-500 px-2.5 py-1 text-xs font-medium text-white shadow-sm transition-colors hover:bg-primary-600"
                >
                  Apply edits
                </button>
              </>
            ) : (
              displayContent.length > 0 && (
                <button
                  type="button"
                  onClick={onEditStart}
                  className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-600 shadow-sm transition-colors hover:bg-gray-50"
                >
                  <Pencil className="h-3 w-3" />
                  Edit
                </button>
              )
            )}
          </div>
        </div>

        {hasTableBlocks && !isEditing && (
          <p className="mt-2 text-xs text-gray-500">
            This section includes tables. You can edit text blocks; table layout stays as extracted until we add a grid
            editor.
          </p>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain bg-gray-50/30 px-6 py-4">
        <div className="w-full max-w-none">
          {isEditing && hasTableBlocks ? (
            <>
              {renderBlockEditors(draftContent ?? section.content)}
              {validationError && (
                <p className="mt-3 text-sm text-red-600" role="alert">
                  {validationError}
                </p>
              )}
            </>
          ) : isEditing ? (
            <>
              <textarea
                aria-label="Edit clause text"
                value={contentToText(draftContent ?? displayContent)}
                onChange={(e) => onDraftChange(textToContent(e.target.value))}
                className="flex min-h-[200px] w-full resize-y rounded-lg border border-gray-300 bg-white px-3 py-3 text-[15px] leading-7 text-gray-800 shadow-sm transition-colors focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                rows={12}
              />
              {validationError && (
                <p className="mt-2 text-sm text-red-600" role="alert">
                  {validationError}
                </p>
              )}
            </>
          ) : displayContent.length === 0 ? (
            <p className="text-sm text-gray-500">This section has no content.</p>
          ) : (
            <div className="text-[15px] leading-7 text-gray-800">{renderReadableBlocks(displayContent)}</div>
          )}
        </div>
      </div>

      <div className="shrink-0 space-y-4 border-t border-gray-100 px-6 pb-6 pt-4">
        <div>
          <p className="mb-2 text-sm font-semibold text-gray-900">Business Units</p>
          <div className="flex flex-wrap gap-2">
            {allBusinessUnits.map((label) => (
              <button
                key={label}
                type="button"
                onClick={() => toggleBusinessUnit(label)}
                className={
                  selectedBUs.includes(label)
                    ? "rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-white transition-colors"
                    : "rounded-full border border-gray-300 bg-white px-3 py-1 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50"
                }
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {onContentRiskLevelChange != null && (
          <fieldset>
            <legend className="mb-2 text-sm font-semibold text-gray-900">Content decision</legend>
            <p className="mb-2 text-xs text-gray-500">
              Select one: ExecDecision (green), BusinessDecision (amber), or LegalDecision (red). Stored with
              the approval and in knowledge-base files.
            </p>
            <div className="flex flex-wrap gap-4">
              {CONTENT_RISK_LEVELS.map((level) => (
                <label
                  key={level}
                  className="flex cursor-pointer items-center gap-2 text-sm text-gray-800"
                >
                  <input
                    type="radio"
                    name="content-risk-level"
                    value={level}
                    checked={contentRiskLevel === level}
                    onChange={() => onContentRiskLevelChange(level)}
                    className="h-4 w-4 border-gray-300 text-primary-500 focus:ring-primary-500"
                  />
                  <span
                    className={
                      level === "ExecDecision"
                        ? "font-medium text-emerald-700"
                        : level === "BusinessDecision"
                          ? "font-medium text-amber-700"
                          : "font-medium text-red-700"
                    }
                  >
                    {level}
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
        )}

        <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={excluded}
            onChange={(e) => setExcludedState(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-primary-500 focus:ring-primary-500"
          />
          <span>Exclude from template</span>
        </label>

        {onSectionSave != null && onSectionCancel != null && (
          <div className="flex justify-end gap-3 border-t border-gray-200 pt-4">
            <Button onClick={onSectionSave} disabled={!saveAllowed || isSaving}>
              {isSaving ? "Saving…" : "Save"}
            </Button>
            <Button variant="ghost" onClick={onSectionCancel} disabled={!hasUnsavedEdits || isSaving}>
              Cancel
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
